import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { AuthenticationService } from '@app/auth/services/authentication.service';
import { Activity } from '@app/core/interfaces/c5/enumerations';
import { RichPresenceDef } from '@app/core/interfaces/c5/rich-presence-def';
import { SecurityPermissionName } from '@app/core/models/permission';
import { NotificationService } from '@app/core/services/notification.service';
import { RouterService } from '@app/routing/router.service';
import { DropdownMenuComponent } from '@app/shared/components/dropdown-menu/dropdown-menu.component';
import { TippyDirective } from '@app/shared/directives/tippy.directive';
import { EndUserContextService } from '@app/shared/services/context/end-user-context.service';
import { ErrorTransformerService } from '@app/shared/services/error-transformer.service';
import { Cached } from '@app/utils/cached';
import { ErrorDescription } from '@app/utils/error/error-description';
import { PortalError } from '@app/utils/error/portal-error';
import { RichPresenceActivityStyle } from '@business/rich-presence/components/rich-presence-state/rich-presence-activity-style';
import { RichPresenceStateViewModel } from '@business/rich-presence/components/rich-presence-state/rich-presence-state-view-model';
import { RichPresenceStyles } from '@business/rich-presence/models/rich-presence-styles';
import { RichPresenceService } from '@business/rich-presence/rich-presence.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { finalize } from 'rxjs/operators';
import { Placement } from 'tippy.js';

@UntilDestroy()
@Component({
  selector: 'portal-rich-presence-state',
  templateUrl: './rich-presence-state.component.html',
  standalone: false,
})
export class RichPresenceStateComponent implements OnInit {
  @Input()
  dropdownPlacement: Placement;

  @Output()
  change = new EventEmitter<RichPresenceDef>();

  @ViewChild('dropdown')
  dropdown: DropdownMenuComponent;

  @ViewChild('errorTippy')
  errorTippy: TippyDirective;

  availableStates: RichPresenceDef[] = [];
  canEnableProfiles: boolean = false;
  enablingProfiles: boolean = false;
  error: ErrorDescription | null = null;
  initializationError: PortalError[] = [];
  loading: boolean = true;
  note: string = '';
  noteChanged: boolean = false;
  state: RichPresenceStateViewModel | null = null;

  styles: Record<string, RichPresenceActivityStyle> = RichPresenceStyles;

  private presenceCached: Cached<RichPresenceStateViewModel>;
  private previousState: RichPresenceStateViewModel | null = null;

  get style(): RichPresenceActivityStyle | null {
    return this.styles[this.state?.enhancedActivity ?? this.state?.activity] ?? null;
  }

  constructor(
    private authenticationService: AuthenticationService,
    private errorTransformerService: ErrorTransformerService,
    private notificationService: NotificationService,
    private richPresenceService: RichPresenceService,
    private endUserContextService: EndUserContextService,
    private routerService: RouterService
  ) {}

  ngOnInit(): void {
    this.presenceCached = this.richPresenceService.endUserPresenceState;

    this.richPresenceService.availablePresenceStates.data$.pipe(untilDestroyed(this)).subscribe((states) => {
      this.availableStates = states;
    });

    const canActivatePresenceProfileMechanism = this.authenticationService.hasPermission(
      SecurityPermissionName.ACTIVATE_PRESENCE_PROFILE_MECHANISM
    );
    const canManagePresenceProfile = this.authenticationService.hasPermission(
      SecurityPermissionName.MANAGE_PRESENCE_PROFILE
    );
    if (canActivatePresenceProfileMechanism && canManagePresenceProfile) {
      this.endUserContextService
        .hasPresenceProfilesModeActivated$()
        .pipe(untilDestroyed(this))
        .subscribe((hasPresenceProfilesModeActivated) => {
          this.canEnableProfiles = !hasPresenceProfilesModeActivated;
        });
    }

    this.presenceCached.loading$.pipe(untilDestroyed(this)).subscribe((loading) => {
      this.loading = loading;
    });

    this.presenceCached.data$.pipe(untilDestroyed(this)).subscribe((presence) => {
      // Do not update the note if the user changed it manually
      if (this.state == null || this.note === this.state.note) {
        this.note = presence.note ?? '';
      }

      this.initializationError = null;
      this.state = presence;
    });

    this.presenceCached.error$.pipe(untilDestroyed(this)).subscribe((error) => {
      if (error != null) {
        this.initializationError = [error];
        this.loading = false;
      }
    });
  }

  enableProfiles(): void {
    this.enablingProfiles = true;

    this.richPresenceService
      .enableProfiles()
      .pipe(
        finalize(() => {
          this.dropdown.tippy.hide();
          this.enablingProfiles = false;
        })
      )
      .subscribe({
        error: (error) => {
          const errorDescription = this.errorTransformerService.describeError(error);

          this.notificationService.showDanger({
            key: 'richPresence.profiles.enable.error',
            parametersToTranslate: {
              error: errorDescription.message ?? errorDescription.title,
            },
          });
        },
        next: () => {
          this.dropdown.tippy.hide();
          this.routerService.navigateToRoute('end-user-rich-presence-profiles');
        },
      });
  }

  onNoteInput(updatedNote: string): void {
    this.note = updatedNote;
    this.noteChanged = true;
  }

  updateAfterDropdownClosed(): void {
    if (this.loading === false) {
      this.updateNote();
    }
  }

  updateNote() {
    if (this.note !== this.state.note && this.noteChanged) {
      this.sendUpdate({
        note: this.note,
      });
    }
  }

  updateState(state: RichPresenceDef): void {
    this.sendUpdate({
      state,
    });
  }

  private sendUpdate({ state, note }: Update) {
    const newActivity = state?.activity ?? this.state.realActivity;
    const newNote = note ?? this.note;
    const newStateDef = state ??
      this.availableStates.find((state) => state.activity === newActivity) ?? {
        activity: Activity.UNKNOWN,
      };
    const newLabel = newStateDef?.label;

    this.error = null;
    this.loading = true;

    this.previousState = {
      ...this.state,
    };

    this.state = {
      activity: newStateDef.refActivity ?? newStateDef.activity,
      enhancedActivity: undefined,
      label: newLabel,
      note: newNote,
      realActivity: newActivity,
    };

    this.richPresenceService
      .setEndUserPresenceState({
        activity: newActivity,
        note: newNote,
      })
      .pipe(
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe({
        error: (error: PortalError) => {
          this.error = this.errorTransformerService.describeError(error);

          if (this.previousState != null) {
            this.note = this.previousState.note ?? '';
            this.noteChanged = false;
            this.state = {
              ...this.previousState,
            };
          }

          this.errorTippy.show();

          window.requestAnimationFrame(() => {
            this.errorTippy.update();
          });
        },
        next: () => {
          this.noteChanged = false;
        },
      });
  }
}

interface Update {
  note?: string;
  state?: RichPresenceDef;
}
